import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash, Eye } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common";
import { TextField, AreaField, SelectField } from "@/components/fields";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const EMPTY = { tipo: "fisica", nombre: "", rfc: "", telefono: "", email: "", direccion: "", banco: "", cuenta: "", clabe: "", notas: "" };

export default function Owners() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const navigate = useNavigate();

  const load = () => api.get("/owners").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (o) => { setForm(o); setEditId(o.id); setOpen(true); };

  const save = async () => {
    if (!form.nombre) return toast.error("El nombre es obligatorio");
    try {
      if (editId) await api.put(`/owners/${editId}`, form);
      else await api.post("/owners", form);
      toast.success("Propietario guardado");
      setOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar propietario?")) return;
    await api.delete(`/owners/${id}`);
    toast.success("Eliminado");
    load();
  };

  return (
    <div data-testid="owners-page">
      <PageHeader
        title="Propietarios"
        subtitle="Expediente financiero y operativo de cada propietario"
        actions={<Button data-testid="new-owner-btn" onClick={openNew} className="rounded-sm gap-2"><Plus size={16} /> Nuevo propietario</Button>}
      />

      {items.length === 0 ? (
        <EmptyState title="Aún no hay propietarios" hint="Registra al primer propietario para vincular inmuebles y contratos." action={<Button onClick={openNew} className="rounded-sm gap-2"><Plus size={16} /> Nuevo propietario</Button>} />
      ) : (
        <div className="bg-card border border-border rounded-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>RFC</TableHead>
                <TableHead>Teléfono</TableHead>
                <TableHead>Correo</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((o) => (
                <TableRow key={o.id} data-testid={`owner-row-${o.id}`} className="cursor-pointer" onClick={() => navigate(`/propietarios/${o.id}`)}>
                  <TableCell className="font-medium">{o.nombre}</TableCell>
                  <TableCell className="capitalize">{o.tipo}</TableCell>
                  <TableCell>{o.rfc || "—"}</TableCell>
                  <TableCell>{o.telefono || "—"}</TableCell>
                  <TableCell>{o.email || "—"}</TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" className="rounded-sm" data-testid={`view-owner-${o.id}`} onClick={() => navigate(`/propietarios/${o.id}`)}><Eye size={16} /></Button>
                    <Button variant="ghost" size="icon" className="rounded-sm" onClick={() => openEdit(o)}>✎</Button>
                    <Button variant="ghost" size="icon" className="rounded-sm text-red-600" data-testid={`del-owner-${o.id}`} onClick={() => del(o.id)}><Trash size={16} /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Editar propietario" : "Nuevo propietario"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <SelectField label="Tipo" value={form.tipo} onChange={set("tipo")} testId="owner-tipo" options={[{ value: "fisica", label: "Persona física" }, { value: "moral", label: "Persona moral" }]} />
            <TextField label="Nombre / Razón social" value={form.nombre} onChange={set("nombre")} testId="owner-nombre" />
            <TextField label="RFC" value={form.rfc} onChange={set("rfc")} testId="owner-rfc" />
            <TextField label="Teléfono" value={form.telefono} onChange={set("telefono")} testId="owner-telefono" />
            <TextField label="Correo" value={form.email} onChange={set("email")} testId="owner-email" />
            <TextField label="Dirección" value={form.direccion} onChange={set("direccion")} testId="owner-direccion" />
            <TextField label="Banco" value={form.banco} onChange={set("banco")} testId="owner-banco" />
            <TextField label="Cuenta" value={form.cuenta} onChange={set("cuenta")} testId="owner-cuenta" />
            <TextField label="CLABE" value={form.clabe} onChange={set("clabe")} testId="owner-clabe" />
            <AreaField label="Notas" value={form.notas} onChange={set("notas")} testId="owner-notas" />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button className="rounded-sm" data-testid="save-owner-btn" onClick={save}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
